import { getEditorAllNotes } from "@/lib/editor-storage";
import { type Content, Extension, createNodeFromContent } from "@tiptap/core";
import { DOMParser, Fragment, type Node as ProseMirrorNode } from "@tiptap/pm/model";
import { safeMarkdown } from "../../../lib/encoding";
import { linkify } from "../../../lib/link-utils";
import { normalizeMarkdownPaste } from "../../../lib/markdown-paste/normalize-markdown";
import { MarkdownClipboard } from "./extensions/tiptap/clipboard";
import { MarkdownTightLists } from "./extensions/tiptap/tight-lists";
import { MarkdownParser } from "./parse/MarkdownParser";
import { MarkdownSerializer } from "./serialize/MarkdownSerializer";
import { elementFromString } from "./util/dom";

type MarkdownOptions = {
    html: boolean;
    breaks: boolean;
    tightLists: boolean;
    inlineMath: boolean;
    bulletListMarker: string;
    tightListClass: string;
    transformCopiedText: boolean;
    transformPastedText: boolean;
    onBeforePaste: (text: string) => string;
};

type MarkdownStorage = {
    options: Record<string, unknown>;
    parser: MarkdownParser | null;
    serializer: MarkdownSerializer | null;
    getMarkdown: (() => string) | null;
};

declare module "@tiptap/core" {
    interface Storage {
        markdown: {
            options: Record<string, unknown>;
            parser: MarkdownParser | null;
            serializer: MarkdownSerializer | null;
            getMarkdown: (() => string) | null;
        };
    }
    interface Editor {
        getMarkdown(): string;
    }
    interface EditorOptions {
        initialContent?: Content;
    }
}

export const Markdown = Extension.create<MarkdownOptions, MarkdownStorage>({
    name: "markdown",
    priority: 50,
    markdownOptions: { indentsContent: true },
    addOptions() {
        return {
            html: true,
            breaks: true,
            tightLists: true,
            inlineMath: true,
            bulletListMarker: "-",
            tightListClass: "tight",
            transformCopiedText: true,
            transformPastedText: true,
            onBeforePaste: (text: string) => {
                return linkify(normalizeMarkdownPaste(safeMarkdown(text)));
            },
        };
    },
    addStorage() {
        return {
            options: {},
            parser: null,
            serializer: null,
            getMarkdown: null,
        };
    },
    addCommands() {
        return {
            setContent:
                (content, options) =>
                    ({ editor, tr, dispatch }) => {
                        if (typeof content !== "string") {
                            return false;
                        }
                        const storage = (this as any).storage || editor.storage.markdown;
                        if (!storage || !storage.parser) {
                            return false;
                        }
                        try {
                            const html = storage.parser.parse(content);
                            if (dispatch) {
                                const element = elementFromString(html);
                                const doc = DOMParser.fromSchema(editor.schema).parse(element, {
                                    ...editor.options.parseOptions,
                                    ...options?.parseOptions,
                                });
                                const transaction = tr
                                    .setMeta("preventUpdate", true)
                                    .replaceWith(0, tr.doc.content.size, doc);
                                if (options?.emitUpdate) {
                                    transaction.setMeta("preventUpdate", false);
                                }
                            }
                            return true;
                        } catch (e) {
                            console.error("Markdown setContent error:", e);
                            return false;
                        }
                    },
            insertContentAt:
                (position, content) =>
                    ({ editor, tr, dispatch }) => {
                        if (typeof content !== "string") {
                            try {
                                const node = createNodeFromContent(content, editor.schema, {
                                    parseOptions: {
                                        preserveWhitespace: "full",
                                        ...editor.options.parseOptions,
                                    },
                                });
                                if (dispatch) {
                                    let from = typeof position === "number" ? position : position.from;
                                    let to = typeof position === "number" ? position : position.to;
                                    const nodes =
                                        node instanceof Fragment
                                            ? Array.from({ length: node.childCount }, (_, i) => node.child(i))
                                            : [node];
                                    const isOnlyBlockContent = nodes.every((n) => n.isBlock);
                                    if (from === to && isOnlyBlockContent) {
                                        const { parent } = tr.doc.resolve(from);
                                        if (parent.isTextblock && !parent.type.spec.code && !parent.childCount) {
                                            from -= 1;
                                            to += 1;
                                        }
                                    }
                                    tr.replaceWith(from, to, node);
                                }
                                return true;
                            } catch (e) {
                                console.error("Markdown insertContentAt error:", e);
                                return false;
                            }
                        }
                        const storage = (this as any).storage || editor.storage.markdown;
                        if (!storage || !storage.parser) {
                            return false;
                        }
                        try {
                            const html = storage.parser.parse(content, {
                                inline: true,
                            });
                            if (dispatch) {
                                const element = elementFromString(html);
                                const doc = DOMParser.fromSchema(editor.schema).parse(element);
                                const from = typeof position === "number" ? position : position.from;
                                const to = typeof position === "number" ? position : position.to;
                                tr.replaceWith(from, to, doc);
                            }
                            return true;
                        } catch (e) {
                            console.error("Markdown insertContentAt error:", e);
                            return false;
                        }
                    },
        };
    },
    onBeforeCreate() {
        this.storage.options = { ...this.options };
        this.storage.parser = new MarkdownParser(this.editor, this.options);
        this.storage.serializer = new MarkdownSerializer(this.editor);
        let previousDocument: ProseMirrorNode | null = null;
        let previousSerializer: MarkdownSerializer | null = null;
        let previousOptions = "";
        let previousNote: unknown;
        // weak: the cache key must not keep an old array of every note alive
        let previousNotes: WeakRef<object> | undefined;
        let previousMarkdown = "";
        const getMarkdown = (): string => {
            const serializer = this.storage.serializer;
            if (!serializer || !this.editor.schema || !this.editor.extensionManager || !this.editor.state?.doc) return "";
            const document = this.editor.state.doc;
            const options = JSON.stringify(this.storage.options);
            const { note } = this.editor.storage as { note?: unknown };
            const notes = getEditorAllNotes(this.editor);
            if (document === previousDocument && serializer === previousSerializer &&
                options === previousOptions && note === previousNote && notes === previousNotes?.deref()) return previousMarkdown;
            const markdown = serializer.serialize(document);
            previousDocument = document;
            previousSerializer = serializer;
            previousOptions = options;
            previousNote = note;
            previousNotes = new WeakRef(notes);
            previousMarkdown = markdown;
            return markdown;
        };
        this.storage.getMarkdown = getMarkdown;
        this.editor.getMarkdown = getMarkdown;
        if (this.editor.options.content && this.storage.parser) {
            this.editor.options.initialContent = this.editor.options.content;
            this.editor.options.content = this.storage.parser.parse(this.editor.options.content);
        }
    },
    onCreate() {
        this.editor.options.content = this.editor.options.initialContent ?? null;
        delete this.editor.options.initialContent;
    },
    addExtensions() {
        return [
            MarkdownTightLists.configure({
                tight: this.options.tightLists,
                tightClass: this.options.tightListClass,
            }),
            MarkdownClipboard.configure({
                onBeforePaste: this.options.onBeforePaste,
                transformCopiedText: this.options.transformCopiedText,
                transformPastedText: this.options.transformPastedText,
            }),
        ];
    },
});
